import React, { useState, useEffect, useRef } from 'react';
import { Save, LogOut, ArrowLeft, Mail, User as UserIcon, Loader2, LogIn, KeyRound, X, AlertCircle, ExternalLink, RefreshCw, Settings, ShieldCheck, Users, Plus, Edit2, Trash2, Shield, UserCheck, Sparkles, BookOpen, FileText, MessageSquare, FolderOpen, Star, ChevronLeft, ChevronRight, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Dialog, DialogType } from './ui/Dialog';
import { ProfileAvatar } from './ProfileAvatar';
import { GeminiApiKeySettings } from './GeminiApiKeySettings';
import { UserProfile } from '../types';
import { useAuth } from '../AuthProvider';
import { FavoriteAppsSection } from './FavoriteAppsSection';
import { HeaderNavbar } from './HeaderNavbar';
import { ProfileOverview } from './ProfileOverview';
import { WalletSection } from './WalletSection';
import { TopUpModal } from './TopUpModal';
import { AvatarPickerModal } from './AvatarPickerModal';
import { AdminPortal } from './AdminPortal';
import { OtpVerificationForm } from './OtpVerificationForm';
import { GoogleLoginButton } from './GoogleLoginButton';
import { encryptApiKey } from '../utils/security';
import { QuestionBankModule } from './questions/QuestionBankModule';

export interface AppCatalogItem {
  id: string;
  name: string;
  description?: string;
  url: string;
  icon?: React.ReactNode;
  bgColor?: string;
  tag?: string;
}

export const getRootDomain = (): string => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname.split('.').slice(-2).join('.');
  }
  return 'nhagiaoso.com';
};

export const buildAppCatalog = (domain: string): AppCatalogItem[] => [
  {
    id: 'skkn',
    name: 'Trợ lý Viết SKKN',
    description: 'Tạo và hoàn thiện Sáng kiến kinh nghiệm AI chuẩn định dạng Bộ GD&ĐT',
    url: `https://skkn.${domain}`,
    icon: <Sparkles className="w-4 h-4 text-indigo-600" />,
    bgColor: 'bg-indigo-50',
    tag: 'Tích hợp AI'
  },
  {
    id: 'giaolan',
    name: 'Trợ lý Soạn Giáo án',
    description: 'Xây dựng Kế hoạch bài dạy (KHBD) Công văn 5512 nhanh chóng',
    url: `https://giaolan.${domain}`,
    icon: <BookOpen className="w-4 h-4 text-blue-600" />,
    bgColor: 'bg-blue-50',
    tag: 'CV 5512'
  },
  {
    id: 'dekiemtra',
    name: 'Ma trận & Đề kiểm tra AI',
    description: 'Tạo ma trận, đặc tả và đề thi tự luận/trắc nghiệm theo chuẩn',
    url: `https://dekiemtra.${domain}`,
    icon: <FileText className="w-4 h-4 text-emerald-600" />,
    bgColor: 'bg-emerald-50',
    tag: 'Đề thi'
  },
  {
    id: 'chat',
    name: 'Chatbot AI Giáo dục',
    description: 'Trợ lý AI hỏi đáp chuyên sâu, tra cứu văn bản và tài liệu giảng dạy',
    url: `https://chat.${domain}`,
    icon: <MessageSquare className="w-4 h-4 text-purple-600" />,
    bgColor: 'bg-purple-50',
    tag: 'Chatbot'
  },
  {
    id: 'hoso',
    name: 'Hồ sơ Giáo viên Điện tử',
    description: 'Quản lý lịch giảng dạy, hồ sơ cá nhân và lưu trữ giáo án trực tuyến',
    url: `https://hoso.${domain}`,
    icon: <FolderOpen className="w-4 h-4 text-amber-600" />,
    bgColor: 'bg-amber-50',
    tag: 'Hồ sơ'
  },
  {
    id: 'slide',
    name: 'Trợ lý Tạo Bài giảng Slide AI',
    description: 'Thiết kế bài giảng tương tác và thuyết trình tự động chuyên nghiệp',
    url: `https://slide.${domain}`,
    icon: <Sparkles className="w-4 h-4 text-pink-600" />,
    bgColor: 'bg-pink-50',
    tag: 'Slide AI'
  },
  {
    id: 'canva',
    name: 'Thiết kế Đồ họa Giáo dục',
    description: 'Bộ mẫu thiết kế infographic, thiệp và hình ảnh minh họa bài học',
    url: 'https://canva.com',
    icon: <Star className="w-4 h-4 text-cyan-600" />,
    bgColor: 'bg-cyan-50',
    tag: 'Thiết kế'
  },
  {
    id: 'tracnghiem',
    name: 'Ngân hàng Câu hỏi Trắc nghiệm',
    description: 'Kho câu hỏi phân loại theo khối lớp, môn học và mức độ tư duy',
    url: `https://tracnghiem.${domain}`,
    icon: <FileText className="w-4 h-4 text-teal-600" />,
    bgColor: 'bg-teal-50',
    tag: 'Ngân hàng đề'
  },
  {
    id: 'slido',
    name: 'Tương tác Lớp học Trực tuyến',
    description: 'Tạo bình chọn, thăm khảo ý kiến và câu hỏi tương tác thời gian thực',
    url: 'https://slido.com',
    icon: <MessageSquare className="w-4 h-4 text-orange-600" />,
    bgColor: 'bg-orange-50',
    tag: 'Tương tác'
  },
  {
    id: 'kahoot',
    name: 'Trò chơi Trắc nghiệm Lớp học',
    description: 'Tổ chức các buổi ôn tập học tập thông qua trò chơi vui nhộn',
    url: 'https://kahoot.com',
    icon: <Sparkles className="w-4 h-4 text-red-600" />,
    bgColor: 'bg-red-50',
    tag: 'Trò chơi'
  },
  {
    id: 'padlet',
    name: 'Bảng Tương tác Nhóm',
    description: 'Bảo thảo luận trực tuyến, thu thập sản phẩm học tập của học sinh',
    url: 'https://padlet.com',
    icon: <BookOpen className="w-4 h-4 text-rose-600" />,
    bgColor: 'bg-rose-50',
    tag: 'Thảo luận'
  },
  {
    id: 'quizizz',
    name: 'Kiểm tra & Ôn tập Quizizz',
    description: 'Hệ thống bài tập tự luyện và thi đua kiến thức cho học sinh',
    url: 'https://quizizz.com',
    icon: <FileText className="w-4 h-4 text-violet-600" />,
    bgColor: 'bg-violet-50',
    tag: 'Ôn tập'
  }
];

const DEFAULT_APP_CATALOG: AppCatalogItem[] = buildAppCatalog(getRootDomain());

/**
 * Mapping dynamic user.favorites from MongoDB DB with app catalog
 */
const resolveFavorites = (userFavorites?: any[]): AppCatalogItem[] => {
  if (!userFavorites || !Array.isArray(userFavorites) || userFavorites.length === 0) {
    return DEFAULT_APP_CATALOG;
  }

  return userFavorites.map((fav, index) => {
    if (typeof fav === 'string') {
      const lower = fav.toLowerCase().trim();
      const found = DEFAULT_APP_CATALOG.find(
        app => app.id.toLowerCase() === lower || app.url.toLowerCase() === lower || app.name.toLowerCase().includes(lower)
      );
      if (found) return found;

      if (fav.startsWith('http://') || fav.startsWith('https://')) {
        let domainName = fav;
        try {
          domainName = new URL(fav).hostname.replace('www.', '');
        } catch (e) {}
        return {
          id: `fav-${index}-${fav}`,
          name: domainName,
          description: `Ứng dụng liên kết: ${fav}`,
          url: fav,
          icon: <ExternalLink className="w-4 h-4 text-indigo-600" />,
          bgColor: 'bg-slate-50',
          tag: 'Liên kết'
        };
      }

      return {
        id: `fav-${index}-${fav}`,
        name: fav,
        description: 'Ứng dụng trong hệ sinh thái Nhà Giáo Số',
        url: '#',
        icon: <Sparkles className="w-4 h-4 text-indigo-600" />,
        bgColor: 'bg-indigo-50',
        tag: 'Yêu thích'
      };
    }

    if (typeof fav === 'object' && fav !== null) {
      const matchedCatalog = fav.id ? DEFAULT_APP_CATALOG.find(a => a.id === fav.id) : null;
      return {
        id: fav.id || `fav-${index}`,
        name: fav.name || fav.title || matchedCatalog?.name || 'Ứng dụng',
        description: fav.description || matchedCatalog?.description || '',
        url: fav.url || matchedCatalog?.url || '#',
        icon: matchedCatalog?.icon || <Sparkles className="w-4 h-4 text-indigo-600" />,
        bgColor: fav.bgColor || matchedCatalog?.bgColor || 'bg-indigo-50',
        tag: fav.tag || matchedCatalog?.tag || 'Yêu thích'
      };
    }

    return {
      id: `fav-${index}`,
      name: 'Ứng dụng',
      description: '',
      url: '#',
      icon: <Sparkles className="w-4 h-4 text-indigo-600" />,
      bgColor: 'bg-indigo-50',
      tag: 'Yêu thích'
    };
  });
};
import { useGeneralSettings } from '../hooks/useQueries';
import {
  API_BASE_URL,
  getStoredToken,
  setStoredToken,
  loginApi,
  registerApi,
  getUserProfileApi,
  updateUserProfileApi,
  uploadMediaApi,
  customFetch,
  logoutApi,
  changePasswordApi,
  forgotPasswordApi,
  resetPasswordApi,
  getUsersApi,
  createUserApi,
  updateUserApi,
  deleteUserApi,
  updateUserRoleApi
} from '../services/apiService';

interface ProfilePageProps {
  initialData?: UserProfile;
}

const GRADE_LEVELS = [
  'Mẫu giáo',
  'Lớp 1', 'Lớp 2', 'Lớp 3', 'Lớp 4', 'Lớp 5',
  'Lớp 6', 'Lớp 7', 'Lớp 8', 'Lớp 9',
  'Lớp 10', 'Lớp 11', 'Lớp 12',
  'CĐ/ĐH', 'Cao học'
];

const VIETNAM_PROVINCES = [
  "An Giang", "Bà Rịa - Vũng Tàu", "Bắc Giang", "Bắc Kạn", "Bạc Liêu", "Bắc Ninh", 
  "Bến Tre", "Bình Định", "Bình Dương", "Bình Phước", "Bình Thuận", "Cà Mau", 
  "Cần Thơ", "Cao Bằng", "Đà Nẵng", "Đắc Lắk", "Đắc Nông", "Điện Biên", 
  "Đồng Nai", "Đồng Tháp", "Gia Lai", "Hà Giang", "Hà Nam", "Hà Nội", 
  "Hà Tĩnh", "Hải Dương", "Hải Phòng", "Hậu Giang", "Hòa Bình", "Hưng Yên", 
  "Khánh Hòa", "Kiên Giang", "Kon Tum", "Lai Châu", "Lâm Đồng", "Lạng Sơn", 
  "Lào Cai", "Long An", "Nam Định", "Nghệ An", "Ninh Bình", "Ninh Thuận", 
  "Phú Thọ", "Phú Yên", "Quảng Bình", "Quảng Nam", "Quảng Ngãi", "Quảng Ninh", 
  "Quảng Trị", "Sóc Trăng", "Sơn La", "Tây Ninh", "Thái Bình", "Thái Nguyên", 
  "Thanh Hóa", "Thừa Thiên Huế", "Tiền Giang", "TP Hồ Chí Minh", "Trà Vinh", 
  "Tuyên Quang", "Vĩnh Long", "Vĩnh Phúc", "Yên Bái"
];

const APP_NAMES: Record<string, string> = {
  'skkn': 'Trợ lý Viết SKKN',
  'giaolan': 'Trợ lý Giáo án',
  'dekiemtra': 'Ma trận Đề kiểm tra',
  'chat': 'Chatbot AI',
  'hoso': 'Hồ sơ Giáo viên',
  'slide': 'Trợ lý Bài giảng Slide',
  'tracnghiem': 'Ngân hàng Câu hỏi Trắc nghiệm',
  'localhost': 'Môi trường Test'
};

// Helper functions for safe formatting (prevents white-screen crashes on null/undefined values)
const formatSafeDate = (dateVal?: string | Date | null): string => {
  if (!dateVal) return 'Chưa cập nhật';
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return 'Chưa cập nhật';
    return d.toLocaleString('vi-VN');
  } catch {
    return 'Chưa cập nhật';
  }
};

const formatSafeNumber = (numVal?: number | null): string => {
  if (numVal === undefined || numVal === null || isNaN(Number(numVal))) return '0';
  try {
    return Number(numVal).toLocaleString('vi-VN');
  } catch {
    return String(numVal);
  }
};

type SyncResult = 'SUCCESS' | 'NETWORK_ERROR' | 'COOKIE_WRITE_FAILED';

export const ProfilePage: React.FC<ProfilePageProps> = ({ initialData }) => {
  const { user: authUser, loading: authLoading, setUser: setAuthUser, logout: authLogout, loginSuccess, refetchProfile } = useAuth();
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(initialData || authUser || null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToastMessage({ type, message });
    setTimeout(() => {
      setToastMessage(prev => (prev?.message === message ? null : prev));
    }, 4000);
  };

  // Đồng bộ trạng thái người dùng từ AuthProvider duy nhất (tránh gọi lại API nhiều lần)
  useEffect(() => {
    if (!authLoading) {
      if (authUser === null) {
        setCurrentUser(null);
        setAuthMode('login');
        setAuthError(null);
        setLoading(false);
        setSyncStatus('');
        setActiveTabState('profile');
        if (typeof window !== 'undefined' && !window.location.search.includes('returnUrl') && !window.location.search.includes('return_url')) {
          if (window.location.pathname !== '/') {
            window.history.replaceState({ tab: 'profile' }, '', '/');
          }
        }
      } else if (authUser) {
        setCurrentUser(authUser);
        setFormData(prev => ({
          ...prev,
          ...authUser,
          displayName: authUser.displayName || authUser.fullName || prev.displayName,
          gradeLevel: authUser.gradeLevel || prev.gradeLevel,
          province: authUser.province || prev.province,
          workplace: authUser.workplace || prev.workplace,
          bio: authUser.bio || authUser.description || prev.bio
        }));
        setLoading(false);
        setSyncStatus('');

        // Nếu có returnUrl cần chuyển hướng
        const returnUrlParam = new URLSearchParams(window.location.search).get('return_url') || new URLSearchParams(window.location.search).get('returnUrl') || returnUrl;
        if (returnUrlParam) {
          window.location.href = decodeURIComponent(returnUrlParam);
        }
      }
    }
  }, [authUser, authLoading]);

  const [formData, setFormData] = useState<UserProfile>(initialData || {
    displayName: authUser?.displayName || authUser?.fullName || '',
    email: authUser?.email || '',
    photoURL: authUser?.photoURL || null,
    apiKey: authUser?.apiKey || '',
    credits: authUser?.credits || 0,
    gradeLevel: authUser?.gradeLevel || '',
    workplace: authUser?.workplace || '',
    province: authUser?.province || '',
    bio: authUser?.bio || authUser?.description || '',
    role: authUser?.role || 'user'
  });
  
  const [activeTab, setActiveTabState] = useState<'profile' | 'users' | 'wallet' | 'questions'>('profile');
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpInitialTab, setTopUpInitialTab] = useState<'XU' | 'VIP'>('XU');
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

  const handleOpenTopUp = (tab: 'XU' | 'VIP' = 'XU') => {
    setTopUpInitialTab(tab);
    setShowTopUpModal(true);
  };

  const setActiveTab = (tab: 'profile' | 'users' | 'wallet' | 'questions') => {
    setActiveTabState(tab);
    if (typeof window !== 'undefined') {
      let targetPath = '/';
      if (tab === 'wallet') {
        targetPath = '/xu-vip';
      } else if (tab === 'users') {
        targetPath = '/admin';
      } else if (tab === 'questions') {
        targetPath = '/ngan-hang-cau-hoi';
      } else {
        targetPath = '/';
      }
      
      // Khi chuyển đổi giữa các Tab chính (Hồ sơ, Ví Xu - VIP, NH Câu hỏi, Admin),
      // xóa bỏ toàn bộ query params của trang trước để URL luôn sạch sẽ
      if (window.location.pathname !== targetPath || window.location.search) {
        window.history.pushState({ tab }, '', targetPath);
      }
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncTabFromUrl = () => {
      const pathname = window.location.pathname.toLowerCase();
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');

      if (pathname.includes('/xu-vip') || pathname.includes('/nap-xu') || pathname.includes('/wallet') || tabParam === 'wallet' || tabParam === 'xu-vip' || tabParam === 'nap-xu') {
        setActiveTabState('wallet');
      } else if (pathname.includes('/ngan-hang-cau-hoi') || pathname.includes('/questions') || tabParam === 'questions' || tabParam === 'ngan-hang-cau-hoi') {
        setActiveTabState('questions');
      } else if (pathname.includes('/admin') || tabParam === 'admin' || tabParam === 'users') {
        setActiveTabState('users');
      } else {
        setActiveTabState('profile');
      }
    };

    syncTabFromUrl();

    window.addEventListener('popstate', syncTabFromUrl);
    return () => {
      window.removeEventListener('popstate', syncTabFromUrl);
    };
  }, []);
  const [favPage, setFavPage] = useState(1);
  const [showKeyInput, setShowKeyInput] = useState(!initialData?.apiKey); 
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [syncStatus, setSyncStatus] = useState<string>('Đang kiểm tra trạng thái đăng nhập...');
  const [showManualRedirect, setShowManualRedirect] = useState(false);
  const [syncError, setSyncError] = useState<boolean>(false);
  const [loopDetected, setLoopDetected] = useState(false);
  
  // Auth Form State
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'forgot-password' | 'reset-password' | 'verify-otp'>('login');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState(''); 
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [pendingOtpEmail, setPendingOtpEmail] = useState<string>('');
  const [pendingOtpMessage, setPendingOtpMessage] = useState<string | undefined>(undefined);

  // Forgot & Reset Password Flow State
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [submittingAuth, setSubmittingAuth] = useState(false);
  const [forgotSuccessMsg, setForgotSuccessMsg] = useState<string | null>(null);
  const [forgotErrorMsg, setForgotErrorMsg] = useState<string | null>(null);
  const [resetSuccessMsg, setResetSuccessMsg] = useState<string | null>(null);
  const [resetErrorMsg, setResetErrorMsg] = useState<string | null>(null);
  const [generatedTokenForTest, setGeneratedTokenForTest] = useState<string | null>(null);
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [confirmNewPasswordInput, setConfirmNewPasswordInput] = useState(''); 
  const { data: generalSettingsData } = useGeneralSettings();
  const logoUrl = generalSettingsData?.data?.header?.url || 
                  generalSettingsData?.header?.url || 
                  generalSettingsData?.url || 
                  "https://nhagiaoso.com/media/thumbnail/thumb_1785376576637_332682501.png";

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  // Password Modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showModalNewPassword, setShowModalNewPassword] = useState(false);
  const [showModalConfirmPassword, setShowModalConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // User Management State (CRUD REST API)
  const [usersList, setUsersList] = useState<UserProfile[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [userModalForm, setUserModalForm] = useState<Partial<UserProfile>>({
    email: '',
    displayName: '',
    role: 'user',
    credits: 100,
    gradeLevel: 'Lớp 10',
    workplace: '',
    province: ''
  });
  const [savingUserModal, setSavingUserModal] = useState(false);

  // SSO Navigation
  const [returnUrl, setReturnUrl] = useState<string | null>(null);
  const [sourceAppName, setSourceAppName] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const syncAttempted = useRef(false);

  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    type: DialogType;
    title: string;
    message: string;
    onConfirm?: () => void;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  const showDialog = (type: DialogType, title: string, message: string, onConfirm?: () => void) => {
    setDialog({ isOpen: true, type, title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  const maskApiKey = (key: string) => {
    if (!key) return '';
    if (key.length <= 10) return '••••••••'; 
    return `${key.slice(0, 6)}••••••••${key.slice(-6)}`;
  };

  const getAppNameFromUrl = (urlStr: string): string => {
    try {
        const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
        const urlObj = new URL(urlStr);
        const hostname = urlObj.hostname;
        if (APP_NAMES[hostname]) return APP_NAMES[hostname];
        const sub = hostname.replace(`.${rootDomain}`, '').replace('.nhagiaoso.com', '');
        if (APP_NAMES[sub]) return APP_NAMES[sub];
        const parts = hostname.split('.');
        if (parts.length > 0 && parts[0] !== 'www' && parts[0] !== 'user') {
            const appName = parts[0];
            return appName.charAt(0).toUpperCase() + appName.slice(1);
        }
        return "Ứng dụng liên kết";
    } catch (e) {
        return "Ứng dụng";
    }
  };

  const executeRedirect = (urlStr: string) => {
      const lastRedirectRaw = sessionStorage.getItem('ngs_last_redirect');
      if (lastRedirectRaw) {
        try {
          const { url, time } = JSON.parse(lastRedirectRaw);
          if (url === urlStr && (Date.now() - time < 3000)) {
             setSyncStatus("Đang chuyển hướng...");
             setLoopDetected(true);
             setShowManualRedirect(true);
             return; 
          }
        } catch (e) {}
      }
      
      setSyncStatus('Đang chuyển đến ứng dụng...');
      const targetUrl = decodeURIComponent(urlStr);
      sessionStorage.setItem('ngs_last_redirect', JSON.stringify({ url: urlStr, time: Date.now() }));
      setTimeout(() => { window.location.href = targetUrl; }, 150);
  };
  
  // SSO Check & Verification on Mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const returnUrlParam = params.get('return_url') || params.get('returnUrl');
    const tokenFromUrl = params.get('token');

    if (tokenFromUrl) {
      setResetToken(tokenFromUrl);
      setAuthMode('reset-password');
    }

    if (action === 'logout_success') {
      window.history.replaceState({}, '', window.location.pathname);
      setStoredToken(null);
      setCurrentUser(null);
      setLoading(false);
      setSyncStatus('');
      return;
    }

    if (returnUrlParam) {
      const decodedUrl = decodeURIComponent(returnUrlParam);
      setReturnUrl(decodedUrl);
      setSourceAppName(getAppNameFromUrl(decodedUrl));
      setIsRedirecting(true);
      if (authUser) {
        window.location.href = decodedUrl;
      }
    }
  }, [authUser]);

  // Tải danh sách Users (GET /api/users) cho Quản lý User
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const list = await getUsersApi();
      setUsersList(list);
    } catch (err: any) {
      showDialog('error', 'Lỗi', err.message || 'Không thể lấy danh sách người dùng.');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (currentUser && activeTab === 'users') {
      fetchUsers();
    }
  }, [currentUser, activeTab]);

  const handleLoginSuccess = async (loggedInUser: UserProfile) => {
    const profile = { ...loggedInUser };

    // 1. Cập nhật state cục bộ và AuthContext
    setCurrentUser(profile);
    setFormData(prev => ({
      ...prev,
      ...profile,
      displayName: profile.displayName || profile.fullName || prev.displayName,
      gradeLevel: profile.gradeLevel || prev.gradeLevel,
      province: profile.province || prev.province,
      workplace: profile.workplace || prev.workplace,
      bio: profile.bio || profile.description || prev.bio
    }));

    // 2. Kích hoạt loginSuccess trong AuthProvider để gọi và đồng bộ đầy đủ các API backend (/api/protected-profile, /api/library/wallet, v.v.)
    await loginSuccess(profile);

    // 3. Xử lý điều hướng sau khi Đăng nhập thành công:
    // Kiểm tra tham số trên URL: const returnUrl = new URLSearchParams(window.location.search).get('return_url');
    const returnUrlParam = new URLSearchParams(window.location.search).get('return_url') || new URLSearchParams(window.location.search).get('returnUrl');

    if (returnUrlParam) {
      // Nếu có returnUrl: Giải mã và chuyển hướng người dùng về đúng link đó:
      window.location.href = decodeURIComponent(returnUrlParam);
    } else {
      // Nếu KHÔNG có returnUrl: giữ người dùng ở lại trang Hồ sơ.
      setActiveTabState('profile');
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('ngs_last_redirect');
        window.history.replaceState({ tab: 'profile' }, '', '/');
      }
    }
  };

  const handleAuthAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingAuth(true);
    setAuthError(null);
    setSyncStatus('Đang xác thực...');

    try {
      if (authMode === 'register') {
        if (passwordInput !== confirmPasswordInput) {
            setAuthError("Mật khẩu xác nhận không khớp.");
            setSubmittingAuth(false);
            return;
        }
        if (passwordInput.length < 6) {
            setAuthError("Mật khẩu phải có ít nhất 6 ký tự.");
            setSubmittingAuth(false);
            return;
        }
        
        const response = await registerApi(emailInput, passwordInput);
        if (response.requireVerification) {
          setPendingOtpEmail(response.email || emailInput);
          setPendingOtpMessage(response.message);
          setAuthMode('verify-otp');
          setAuthError(null);
          setSubmittingAuth(false);
          return;
        }

        if (response.user) {
          await handleLoginSuccess(response.user);
        }
      } else {
        const response = await loginApi(emailInput, passwordInput);
        if (response.requireVerification) {
          setPendingOtpEmail(response.email || emailInput);
          setPendingOtpMessage(response.message);
          setAuthMode('verify-otp');
          setAuthError(null);
          setSubmittingAuth(false);
          return;
        }

        if (response.user) {
          await handleLoginSuccess(response.user);
        }
      }
    } catch (error: any) {
      setAuthError(error.message || "Đã có lỗi xảy ra khi xác thực.");
    } finally {
      setSubmittingAuth(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingAuth(true);
    setForgotErrorMsg(null);
    setForgotSuccessMsg(null);
    setGeneratedTokenForTest(null);

    if (!emailInput || !emailInput.includes('@')) {
      setForgotErrorMsg("Vui lòng nhập địa chỉ email hợp lệ.");
      setSubmittingAuth(false);
      return;
    }

    try {
      const res = await forgotPasswordApi(emailInput);
      setForgotSuccessMsg(res.message || "Nếu email tồn tại, liên kết đặt lại mật khẩu đã được gửi/khởi tạo.");
      if (res.token) {
        setGeneratedTokenForTest(res.token);
      }
    } catch (err: any) {
      setForgotErrorMsg(err.message || "Đã có lỗi xảy ra khi gửi yêu cầu.");
    } finally {
      setSubmittingAuth(false);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittingAuth(true);
    setResetErrorMsg(null);
    setResetSuccessMsg(null);

    if (newPasswordInput.length < 6) {
      setResetErrorMsg("Mật khẩu mới phải có tối thiểu 6 ký tự.");
      setSubmittingAuth(false);
      return;
    }

    if (newPasswordInput !== confirmNewPasswordInput) {
      setResetErrorMsg("Mật khẩu xác nhận không khớp.");
      setSubmittingAuth(false);
      return;
    }

    if (!resetToken) {
      setResetErrorMsg("Liên kết đã hết hạn hoặc không hợp lệ, vui lòng thử lại.");
      setSubmittingAuth(false);
      return;
    }

    try {
      const res = await resetPasswordApi(resetToken, newPasswordInput);
      setResetSuccessMsg(res.message || "Đặt lại mật khẩu thành công!");

      const newSearch = window.location.search
        .replace(/([?&])token=[^&]*(&|$)/, '$1')
        .replace(/[?&]$/, '');
      const newUrl = window.location.pathname + (newSearch ? newSearch : '');
      window.history.replaceState({}, document.title, newUrl);

      setTimeout(() => {
        setResetToken(null);
        setNewPasswordInput('');
        setConfirmNewPasswordInput('');
        setAuthMode('login');
        setResetSuccessMsg(null);
      }, 2000);
    } catch (err: any) {
      setResetErrorMsg("Liên kết đã hết hạn hoặc không hợp lệ, vui lòng thử lại.");
    } finally {
      setSubmittingAuth(false);
    }
  };

  const handleChange = (field: keyof UserProfile, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const handleAvatarChange = async (base64: string) => {
    setFormData(prev => ({ ...prev, photoURL: base64 }));
    setIsDirty(true);

    try {
      let finalAvatarUrl = base64;
      try {
        const fetchRes = await customFetch(base64);
        const blob = await fetchRes.blob();
        const uploadRes = await uploadMediaApi(blob, 'class/teacher_avatar', `avatar_${Date.now()}.jpg`);
        if (uploadRes.success && uploadRes.url) {
          finalAvatarUrl = uploadRes.url;
        }
      } catch (uploadErr) {
        console.warn("Upload media service fallback to base64:", uploadErr);
      }

      // Lưu đường dẫn avatar mới vào cơ sở dữ liệu qua /api/users/profile
      const res = await updateUserProfileApi({
        photoURL: finalAvatarUrl,
        avatar: finalAvatarUrl
      });

      if (res.success) {
        const finalPhoto = res.user?.photoURL || res.user?.avatar || finalAvatarUrl;
        handleAvatarUpdated(finalPhoto, res.user);
        showToast('success', 'Cập nhật ảnh đại diện thành công!');
      } else {
        showToast('error', res.message || 'Lưu ảnh đại diện thất bại.');
      }
    } catch (err: any) {
      showToast('error', err.message || 'Lỗi khi cập nhật ảnh đại diện.');
    }
  };

  const handleAvatarUpdated = (newPhotoURL: string, updatedUser?: UserProfile) => {
    setFormData(prev => ({ ...prev, photoURL: newPhotoURL }));
    if (updatedUser) {
      setCurrentUser(updatedUser);
      setAuthUser(updatedUser);
    } else {
      setAuthUser(prev => prev ? { ...prev, photoURL: newPhotoURL, avatar: newPhotoURL } : null);
      setCurrentUser(prev => prev ? { ...prev, photoURL: newPhotoURL, avatar: newPhotoURL } : null);
    }
  };

  // Save current profile via PUT /api/users/profile or /api/users/me
  const handleSave = async () => {
    setSaving(true);
    try {
      const formPayload = {
        displayName: formData.displayName || '',
        gradeLevel: formData.gradeLevel || '',
        province: formData.province || '',
        workplace: formData.workplace || '',
        bio: formData.bio || '',
        photoURL: formData.photoURL || undefined,
        avatar: formData.photoURL || undefined
      };

      const result = await updateUserProfileApi(formPayload);

      if (result.success && result.user) {
        setAuthUser(result.user);
        setCurrentUser(result.user);
        setFormData(prev => ({
          ...prev,
          ...result.user,
          displayName: result.user?.displayName || result.user?.fullName || '',
          gradeLevel: result.user?.gradeLevel || '',
          province: result.user?.province || '',
          workplace: result.user?.workplace || '',
          bio: result.user?.bio || result.user?.description || ''
        }));
        setIsDirty(false);
        const successMsg = result.message || "Đã cập nhật thông tin hồ sơ thành công!";
        showToast('success', successMsg);
        showDialog('success', 'Thành công', successMsg);
      } else {
        const errorMsg = result.message || "Cập nhật thông tin hồ sơ thất bại.";
        showToast('error', errorMsg);
        showDialog('error', 'Lỗi cập nhật', errorMsg);
      }
    } catch (error: any) {
      const errorMsg = error.message || "Không thể kết nối máy chủ để cập nhật hồ sơ.";
      showToast('error', errorMsg);
      showDialog('error', 'Lỗi hệ thống', errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    if (newPassword !== confirmPassword) { setPasswordError("Mật khẩu không khớp!"); return; }
    setChangingPassword(true);
    try {
      await changePasswordApi(currentPassword, newPassword);
      setShowPasswordModal(false);
      showDialog('success', 'Thành công', 'Đổi mật khẩu thành công!');
      setNewPassword(''); setConfirmPassword(''); setCurrentPassword('');
    } catch (error: any) {
      setPasswordError(error.message || "Đổi mật khẩu thất bại.");
    } finally { setChangingPassword(false); }
  };

  const handleLogout = () => {
      showDialog('confirm', 'Đăng xuất', 'Bạn có chắc chắn muốn đăng xuất khỏi hệ thống SSO?', async () => {
            await authLogout();
            setCurrentUser(null);
            setActiveTabState('profile');
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem('ngs_last_redirect');
              window.history.replaceState({ tab: 'profile' }, '', '/');
            }
      });
  };

  const handleBack = () => { if (returnUrl) window.location.href = decodeURIComponent(returnUrl); };

  // ==================== USER MANAGEMENT CRUD ACTIONS ==================== //

  const handleOpenAddUserModal = () => {
    setEditingUser(null);
    setUserModalForm({
      email: '',
      displayName: '',
      role: 'user',
      credits: 100,
      gradeLevel: 'Lớp 10',
      workplace: '',
      province: ''
    });
    setShowUserModal(true);
  };

  const handleOpenEditUserModal = (user: UserProfile) => {
    setEditingUser(user);
    setUserModalForm({ ...user });
    setShowUserModal(true);
  };

  const handleSaveUserModal = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingUserModal(true);
    try {
      if (editingUser) {
        const id = editingUser.id || editingUser._id;
        if (!id) return;
        await updateUserApi(id, userModalForm);
        showDialog('success', 'Thành công', 'Đã cập nhật thông tin người dùng.');
      } else {
        await createUserApi(userModalForm);
        showDialog('success', 'Thành công', 'Thêm mới người dùng thành công.');
      }
      setShowUserModal(false);
      fetchUsers();
    } catch (err: any) {
      showDialog('error', 'Lỗi', err.message || 'Thao tác thất bại.');
    } finally {
      setSavingUserModal(false);
    }
  };

  const handleDeleteUser = (user: UserProfile) => {
    const id = user.id || user._id;
    if (!id) return;
    showDialog('confirm', 'Xác nhận xóa', `Bạn có chắc muốn xóa người dùng ${user.email}?`, async () => {
      try {
        await deleteUserApi(id);
        showDialog('success', 'Đã xóa', 'Người dùng đã được xóa thành công.');
        fetchUsers();
      } catch (err: any) {
        showDialog('error', 'Lỗi', err.message || 'Không thể xóa người dùng.');
      }
    });
  };

  const handleToggleRole = async (user: UserProfile) => {
    const id = user.id || user._id;
    if (!id) return;
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    try {
      await updateUserRoleApi(id, newRole);
      showDialog('success', 'Cập nhật quyền', `Đã chuyển quyền của ${user.displayName || user.email} sang '${newRole}'.`);
      fetchUsers();
    } catch (err: any) {
      showDialog('error', 'Lỗi', err.message || 'Thay đổi quyền thất bại.');
    }
  };

  if (loading) {
      return (
          <div className="min-h-screen bg-background flex flex-col items-center justify-center text-slate-500 p-4">
              <Loader2 className={`w-10 h-10 mb-4 text-blue-600 ${syncError || loopDetected ? '' : 'animate-spin'}`} />
              {isRedirecting ? (
                  <div className="text-center animate-in fade-in duration-500 max-w-md w-full">
                      <h3 className="text-lg font-semibold text-slate-800">
                          {syncError ? 'Kết nối gián đoạn' : (loopDetected ? 'Đang thử lại...' : 'Đang chuyển hướng...')}
                      </h3>
                      <p className={`text-sm mt-1 ${syncError || loopDetected ? 'text-red-500 font-medium' : ''}`}>
                          {syncStatus}
                      </p>
                      <div className="mt-6 p-4 bg-white rounded-lg border border-slate-200 shadow-sm max-w-sm mx-auto">
                          <p className="text-xs text-slate-500 mb-3">Đích đến: {sourceAppName}</p>
                          {showManualRedirect && (
                            <Button onClick={() => returnUrl && executeRedirect(returnUrl)} variant="primary" className="w-full text-xs shadow-md" icon={<ExternalLink size={14} />}>Bấm vào đây để tiếp tục ngay</Button>
                          )}
                      </div>
                  </div>
              ) : (
                  <span>{syncStatus}</span>
              )}
          </div>
      );
  }

  // View Unauthenticated (Login / Register / Forgot Password / Reset Password / Verify OTP)
  if (!currentUser) {
    if (authMode === 'verify-otp') {
      return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
          <Card className="w-full max-w-md p-6 sm:p-8 flex flex-col items-center space-y-6">
            <img src={logoUrl} alt="Nhà giáo số" className="h-14 w-auto object-contain mb-2 mx-auto block" />
            <OtpVerificationForm
              email={pendingOtpEmail || emailInput}
              initialMessage={pendingOtpMessage}
              onSuccess={async (verifiedUser) => {
                await handleLoginSuccess(verifiedUser);
              }}
              onBackToLogin={() => {
                setAuthMode('login');
                setAuthError(null);
              }}
            />
          </Card>
          <Dialog
            isOpen={dialog.isOpen}
            type={dialog.type}
            title={dialog.title}
            message={dialog.message}
            onClose={closeDialog}
            onConfirm={dialog.onConfirm}
          />
        </div>
      );
    }

    let formTitle = "Đăng nhập SSO";
    let formSubtitle = returnUrl ? `Vui lòng đăng nhập để tiếp tục đến ${sourceAppName || 'ứng dụng'}` : "Đăng ký/Đăng nhập để có thể sử dụng những tiện ích, ứng dụng Nhà giáo số miễn phí";

    if (authMode === 'forgot-password') {
      formTitle = "Yêu cầu đặt lại mật khẩu";
      formSubtitle = "Nhập email của bạn để nhận liên kết hướng dẫn khôi phục mật khẩu.";
    } else if (authMode === 'reset-password') {
      formTitle = "Đặt lại mật khẩu mới";
      formSubtitle = "Vui lòng nhập mật khẩu mới cho tài khoản của bạn.";
    } else if (authMode === 'register') {
      formTitle = "Tạo tài khoản mới";
      if (!returnUrl) {
        formSubtitle = "Đăng ký/Đăng nhập để có thể sử dụng những tiện ích, ứng dụng Nhà giáo số miễn phí";
      }
    }

    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 flex flex-col items-center space-y-6">
          <img src={logoUrl} alt="Nhà giáo số" className="h-16 w-auto object-contain mb-4 mx-auto block" />
          <h1 className="text-2xl font-bold text-slate-900 text-center">{formTitle}</h1>
          <p className="text-slate-500 text-center text-sm">{formSubtitle}</p>
          
          <div className="w-full space-y-4">
            {authMode === 'forgot-password' && (
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-600 ml-1">Email đăng ký</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type="email"
                      required
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      disabled={submittingAuth}
                      className="w-full bg-white border border-slate-300 rounded-lg py-2.5 pl-10 pr-3 text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none placeholder:text-slate-400 disabled:opacity-60"
                      placeholder="name@example.com"
                    />
                  </div>
                </div>

                {forgotErrorMsg && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 text-center flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span>{forgotErrorMsg}</span>
                  </div>
                )}

                {forgotSuccessMsg && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 text-center font-medium">
                    {forgotSuccessMsg}
                  </div>
                )}

                {generatedTokenForTest && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 space-y-2">
                    <p className="font-semibold">Token đã khởi tạo (Demo / Thử nghiệm):</p>
                    <p className="break-all font-mono text-[10px] bg-white p-1 rounded border border-blue-100">{generatedTokenForTest}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setResetToken(generatedTokenForTest);
                        setAuthMode('reset-password');
                      }}
                      className="w-full py-1.5 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 text-xs transition-colors"
                    >
                      Bấm vào đây để tới màn hình Đặt lại mật khẩu ngay
                    </button>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submittingAuth}
                  className="w-full py-2.5 shadow-md flex items-center justify-center"
                  icon={submittingAuth ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
                >
                  {submittingAuth ? "Đang gửi yêu cầu..." : "Gửi yêu cầu"}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('login');
                    setForgotErrorMsg(null);
                    setForgotSuccessMsg(null);
                  }}
                  className="w-full text-center text-sm text-slate-500 hover:text-slate-700 mt-2 block"
                >
                  Quay lại đăng nhập
                </button>
              </form>
            )}

            {authMode === 'reset-password' && (
              <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-600 ml-1">Mật khẩu mới</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyRound className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={newPasswordInput}
                      onChange={(e) => setNewPasswordInput(e.target.value)}
                      disabled={submittingAuth}
                      className="w-full bg-white border border-slate-300 rounded-lg py-2.5 pl-10 pr-10 text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none placeholder:text-slate-400 disabled:opacity-60"
                      placeholder="Mật khẩu mới (tối thiểu 6 ký tự)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-600 ml-1">Xác nhận mật khẩu mới</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyRound className="h-5 w-5 text-slate-400" />
                    </div>
                    <input
                      type={showConfirmNewPassword ? 'text' : 'password'}
                      required
                      minLength={6}
                      value={confirmNewPasswordInput}
                      onChange={(e) => setConfirmNewPasswordInput(e.target.value)}
                      disabled={submittingAuth}
                      className="w-full bg-white border border-slate-300 rounded-lg py-2.5 pl-10 pr-10 text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none placeholder:text-slate-400 disabled:opacity-60"
                      placeholder="Nhập lại mật khẩu mới"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      {showConfirmNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {resetErrorMsg && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 text-center flex items-center justify-center">
                    <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span>{resetErrorMsg}</span>
                  </div>
                )}

                {resetSuccessMsg && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 text-center font-medium">
                    {resetSuccessMsg}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submittingAuth}
                  className="w-full py-2.5 shadow-md flex items-center justify-center"
                  icon={submittingAuth ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
                >
                  {submittingAuth ? "Đang cập nhật..." : "Cập nhật mật khẩu"}
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setAuthMode('login');
                    setResetErrorMsg(null);
                    setResetSuccessMsg(null);
                  }}
                  className="w-full text-center text-sm text-slate-500 hover:text-slate-700 mt-2 block"
                >
                  Quay lại đăng nhập
                </button>
              </form>
            )}

            {(authMode === 'login' || authMode === 'register') && (
              <div className="space-y-4">
                {/* Google SSO Button */}
                <GoogleLoginButton
                  disabled={submittingAuth}
                  onSuccess={async (user) => {
                    await handleLoginSuccess(user);
                  }}
                  onError={(err) => {
                    setAuthError(err);
                  }}
                />

                {/* Or Divider */}
                <div className="relative flex items-center justify-center my-4">
                  <div className="border-t border-slate-200 w-full"></div>
                  <span className="bg-white px-3 text-xs font-bold text-slate-400 uppercase tracking-wider absolute">
                    hoặc đăng nhập bằng email
                  </span>
                </div>

                {/* Password / Email Form */}
                <form onSubmit={handleAuthAction} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-600 ml-1">Email</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-slate-400" />
                      </div>
                      <input
                        type="email"
                        required
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        disabled={submittingAuth}
                        className="w-full bg-white border border-slate-300 rounded-lg py-2.5 pl-10 pr-3 text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none placeholder:text-slate-400 disabled:opacity-60"
                        placeholder="name@example.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-600 ml-1">Mật khẩu</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <KeyRound className="h-5 w-5 text-slate-400" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={passwordInput}
                        onChange={(e) => setPasswordInput(e.target.value)}
                        disabled={submittingAuth}
                        className="w-full bg-white border border-slate-300 rounded-lg py-2.5 pl-10 pr-10 text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none placeholder:text-slate-400 disabled:opacity-60"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {authMode === 'register' && (
                    <div className="space-y-1 animate-in fade-in slide-in-from-top-2 duration-300">
                      <label className="text-sm font-medium text-slate-600 ml-1">Xác nhận mật khẩu</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <KeyRound className="h-5 w-5 text-slate-400" />
                        </div>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          required
                          value={confirmPasswordInput}
                          onChange={(e) => setConfirmPasswordInput(e.target.value)}
                          disabled={submittingAuth}
                          className="w-full bg-white border border-slate-300 rounded-lg py-2.5 pl-10 pr-10 text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none placeholder:text-slate-400 disabled:opacity-60"
                          placeholder="Nhập lại mật khẩu"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {authMode === 'login' && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setAuthMode('forgot-password');
                          setAuthError(null);
                          setForgotErrorMsg(null);
                          setForgotSuccessMsg(null);
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                      >
                        Quên mật khẩu?
                      </button>
                    </div>
                  )}

                  {authError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 text-center flex items-center justify-center">
                      <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span>{authError}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={submittingAuth}
                    className="w-full py-2.5 shadow-md flex items-center justify-center"
                    icon={submittingAuth ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn size={20} />}
                  >
                    {submittingAuth ? "Đang xử lý..." : (authMode === 'register' ? "Đăng ký" : "Đăng nhập")}
                  </Button>
                </form>
              </div>
            )}
          </div>

          {(authMode === 'login' || authMode === 'register') && (
            <div className="flex items-center space-x-2 text-sm">
              <span className="text-slate-500">{authMode === 'register' ? "Đã có tài khoản?" : "Chưa có tài khoản?"}</span>
              <button
                type="button"
                onClick={() => {
                  setAuthMode(authMode === 'register' ? 'login' : 'register');
                  setAuthError(null);
                }}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                {authMode === 'register' ? "Đăng nhập ngay" : "Đăng ký ngay"}
              </button>
            </div>
          )}

          {returnUrl && (
            <button
              onClick={handleBack}
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center mt-2 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Quay lại {sourceAppName}
            </button>
          )}
        </Card>
        <Dialog
          isOpen={dialog.isOpen}
          type={dialog.type}
          title={dialog.title}
          message={dialog.message}
          onClose={closeDialog}
          onConfirm={dialog.onConfirm}
        />
      </div>
    );
  }

  // Authenticated Profile Main View
  return (
    <div className="min-h-screen bg-background pb-24 md:pb-12">
      {/* Header Navbar */}
      <HeaderNavbar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        returnUrl={returnUrl}
        sourceAppName={sourceAppName}
        onBack={handleBack}
        onOpenTopUp={() => handleOpenTopUp('XU')}
      />

      {/* Main Content Area */}
      <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {activeTab === 'wallet' ? (
          <WalletSection onOpenTopUp={handleOpenTopUp} />
        ) : activeTab === 'questions' ? (
          <QuestionBankModule onOpenTopUp={() => handleOpenTopUp('VIP')} />
        ) : activeTab === 'users' && currentUser?.role === 'admin' ? (
          /* TAB QUẢN TRỊ ADMIN PORTAL */
          <AdminPortal />
        ) : (
          /* TAB HỒ SƠ CÁ NHÂN */
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-4 space-y-6">
              {/* Profile Overview Card & Xu Stat Card */}
              <ProfileOverview
                photoURL={formData.photoURL}
                onAvatarChange={handleAvatarChange}
                onOpenAvatarModal={() => setIsAvatarModalOpen(true)}
                onOpenTopUp={handleOpenTopUp}
                onOpenHistory={() => setActiveTab('wallet')}
              />
            </div>

            <div className="md:col-span-8 space-y-6">
              <Card title="Thông tin Giáo viên">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5 ml-1">Tên hiển thị</label>
                    <input
                      type="text"
                      value={formData.displayName || ''}
                      onChange={(e) => handleChange('displayName', e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg py-2.5 px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      placeholder="Nguyễn Văn A"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5 ml-1">Khối lớp</label>
                    <select
                      value={formData.gradeLevel || ''}
                      onChange={(e) => handleChange('gradeLevel', e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg py-2.5 px-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    >
                      <option value="" disabled>Chọn khối lớp</option>
                      {GRADE_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-1">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5 ml-1">Tỉnh/TP</label>
                    <select
                      value={formData.province || ''}
                      onChange={(e) => handleChange('province', e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg py-2.5 px-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                    >
                      <option value="" disabled>Chọn Tỉnh/TP</option>
                      {VIETNAM_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5 ml-1">Đơn vị công tác</label>
                    <input
                      type="text"
                      value={formData.workplace || ''}
                      onChange={(e) => handleChange('workplace', e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg py-2.5 px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      placeholder="Ví dụ: Quản lý NhaGiaoSo.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5 ml-1">Mô tả bản thân (Bio)</label>
                    <textarea
                      value={formData.bio || ''}
                      onChange={(e) => handleChange('bio', e.target.value)}
                      rows={2}
                      className="w-full bg-white border border-slate-300 rounded-lg py-2.5 px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      placeholder="Giới thiệu bản thân, môn học giảng dạy..."
                    />
                  </div>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
                  {isDirty ? (
                    <span className="text-xs text-amber-600 font-semibold animate-pulse">Có thay đổi chưa lưu</span>
                  ) : (
                    <span className="text-xs text-slate-400">Thông tin đã đồng bộ</span>
                  )}
                  <Button onClick={handleSave} disabled={!isDirty || saving} icon={saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>}>
                    {saving ? "Đang lưu..." : "Lưu thay đổi"}
                  </Button>
                </div>
              </Card>

              <Card title="Cấu hình API và Bảo mật">
                <div className="space-y-4">
                  <GeminiApiKeySettings
                    onKeyUpdated={(updatedUser) => {
                      setFormData(prev => ({ ...prev, ...updatedUser }));
                      setCurrentUser(updatedUser);
                    }}
                  />
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-500 font-medium">Bảo mật tài khoản SSO:</span>
                    <Button variant="secondary" onClick={() => setShowPasswordModal(true)} icon={<KeyRound size={16} />}>
                      Đổi mật khẩu
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Section 2: Danh sách ứng dụng yêu thích */}
              <FavoriteAppsSection />
            </div>
          </div>
        )}
      </main>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-md shadow-2xl relative">
            <button onClick={() => setShowPasswordModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Đổi mật khẩu</h3>
            <form onSubmit={handleChangePasswordSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg py-2.5 pl-3 pr-10 text-slate-900 placeholder:text-slate-400"
                  placeholder="Mật khẩu cũ"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              <div className="relative">
                <input
                  type={showModalNewPassword ? 'text' : 'password'}
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg py-2.5 pl-3 pr-10 text-slate-900 placeholder:text-slate-400"
                  placeholder="Mật khẩu mới"
                />
                <button
                  type="button"
                  onClick={() => setShowModalNewPassword(!showModalNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  {showModalNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              <div className="relative">
                <input
                  type={showModalConfirmPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg py-2.5 pl-3 pr-10 text-slate-900 placeholder:text-slate-400"
                  placeholder="Nhập lại mật khẩu mới"
                />
                <button
                  type="button"
                  onClick={() => setShowModalConfirmPassword(!showModalConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  {showModalConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {passwordError && <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">{passwordError}</div>}
              <div className="flex justify-end space-x-3 pt-2">
                <Button type="button" variant="ghost" onClick={() => setShowPasswordModal(false)}>Hủy</Button>
                <Button type="submit" disabled={changingPassword}>{changingPassword ? "Đang xử lý..." : "Xác nhận"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CRUD User Add/Edit Modal */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-lg shadow-2xl relative">
            <button onClick={() => setShowUserModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">{editingUser ? "Sửa thông tin User" : "Thêm User mới"}</h3>
            <form onSubmit={handleSaveUserModal} className="space-y-4 text-sm">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                <input
                  type="email"
                  required
                  disabled={!!editingUser}
                  value={userModalForm.email || ''}
                  onChange={(e) => setUserModalForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg py-2 px-3 text-slate-900 disabled:bg-slate-100"
                  placeholder="user@nhagiaoso.com"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Tên hiển thị</label>
                <input
                  type="text"
                  value={userModalForm.displayName || ''}
                  onChange={(e) => setUserModalForm(prev => ({ ...prev, displayName: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg py-2 px-3 text-slate-900"
                  placeholder="Nguyễn Văn A"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Số Xu</label>
                  <input
                    type="number"
                    value={userModalForm.credits || 0}
                    onChange={(e) => setUserModalForm(prev => ({ ...prev, credits: Number(e.target.value) }))}
                    className="w-full border border-slate-300 rounded-lg py-2 px-3 text-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Vai trò</label>
                  <select
                    value={userModalForm.role || 'user'}
                    onChange={(e) => setUserModalForm(prev => ({ ...prev, role: e.target.value as 'user' | 'admin' }))}
                    className="w-full border border-slate-300 rounded-lg py-2 px-3 text-slate-900"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Khối lớp</label>
                  <select
                    value={userModalForm.gradeLevel || ''}
                    onChange={(e) => setUserModalForm(prev => ({ ...prev, gradeLevel: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg py-2 px-3 text-slate-900"
                  >
                    <option value="">Chọn khối lớp</option>
                    {GRADE_LEVELS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Tỉnh/TP</label>
                  <select
                    value={userModalForm.province || ''}
                    onChange={(e) => setUserModalForm(prev => ({ ...prev, province: e.target.value }))}
                    className="w-full border border-slate-300 rounded-lg py-2 px-3 text-slate-900"
                  >
                    <option value="">Chọn Tỉnh/TP</option>
                    {VIETNAM_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Đơn vị công tác</label>
                <input
                  type="text"
                  value={userModalForm.workplace || ''}
                  onChange={(e) => setUserModalForm(prev => ({ ...prev, workplace: e.target.value }))}
                  className="w-full border border-slate-300 rounded-lg py-2 px-3 text-slate-900"
                  placeholder="Trường THPT..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-3">
                <Button type="button" variant="ghost" onClick={() => setShowUserModal(false)}>Hủy</Button>
                <Button type="submit" disabled={savingUserModal}>{savingUserModal ? "Đang lưu REST API..." : "Lưu dữ liệu"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* TopUp Modal */}
      <TopUpModal
        isOpen={showTopUpModal}
        onClose={() => setShowTopUpModal(false)}
        initialTab={topUpInitialTab}
      />

      {/* Avatar Picker Modal */}
      <AvatarPickerModal
        isOpen={isAvatarModalOpen}
        currentPhotoURL={formData.photoURL || currentUser?.photoURL || null}
        onClose={() => setIsAvatarModalOpen(false)}
        onAvatarUpdated={handleAvatarUpdated}
        onShowToast={showToast}
      />

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-5 right-5 z-[110] flex items-center space-x-3 px-4 py-3 rounded-xl shadow-2xl border bg-white border-slate-200 animate-in slide-in-from-top-2 duration-300">
          <div className={`p-2 rounded-lg ${toastMessage.type === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
            {toastMessage.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-900">
              {toastMessage.type === 'success' ? 'Thành công' : 'Lỗi'}
            </h4>
            <p className="text-xs font-medium text-slate-600">{toastMessage.message}</p>
          </div>
          <button onClick={() => setToastMessage(null)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg ml-2">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Dialog Alert Component */}
      <Dialog isOpen={dialog.isOpen} type={dialog.type} title={dialog.title} message={dialog.message} onClose={closeDialog} onConfirm={dialog.onConfirm} />
    </div>
  );
};
